<template>
  <div class="production-timeline">
    <div class="timeline-legend">
      <span class="legend-item">
        <span class="legend-dot tl-bar--scheduled"></span>
        {{ $t("productionPriority.timelineScheduled") }}
      </span>
      <span class="legend-item">
        <span class="legend-dot tl-bar--draft"></span>
        {{ $t("productionPriority.timelineDraft") }}
      </span>
      <span class="legend-item">
        <span class="legend-dot tl-day-dot--all"></span>
        {{ $t("productionPriority.dayAll") }}
      </span>
      <span class="legend-item">
        <span class="legend-dot tl-day-dot--some"></span>
        {{ $t("productionPriority.daySome") }}
      </span>
      <span class="legend-item">
        <span class="legend-dot tl-day-dot--none"></span>
        {{ $t("productionPriority.dayNone") }}
      </span>
      <span class="legend-item">
        <span class="legend-dot tl-day-dot--holiday"></span>
        {{ $t("productionPriority.dayHoliday") }}
      </span>
    </div>

    <div class="tl-frame">
      <div class="tl-summary">
        <span class="tl-summary-main">
          <strong>{{ summary.orders }}</strong>
          {{ $t("productionPriority.summaryOrders") }}
        </span>
        <span class="tl-summary-sep">·</span>
        <span class="tl-summary-main">
          <strong>{{ summary.hours.toLocaleString() }}</strong>
          {{ $t("productionPriority.summaryHours") }}
        </span>
        <template v-if="summary.lastEnd">
          <span class="tl-summary-sep">·</span>
          <span
            :class="summary.clamped ? 'tl-summary-warn' : 'tl-summary-main'"
          >
            {{
              $t("productionPriority.summaryUntil", {
                date: formatDate(summary.lastEnd),
              })
            }}
            <template v-if="summary.clamped">
              — {{ $t("productionPriority.summaryBeyond") }}
            </template>
          </span>
        </template>
        <span v-if="searchTerm" class="tl-summary-search">
          {{
            $t("productionPriority.searchHits", {
              n: searchHitCount,
              q: searchLabel,
            })
          }}
        </span>
        <span class="tl-zoom">
          <button
            v-for="z in zoomLevels"
            :key="z"
            type="button"
            class="tl-zoom-btn"
            :class="{ 'is-active': zoom === z }"
            @click="setZoom(z)"
          >
            {{ $t("productionPriority.zoom" + z) }}
          </button>
        </span>
        <span v-if="bulkLoading" class="tl-summary-loading">
          <span class="tl-loading-dot"></span>
          {{
            $t("productionPriority.detailsLoading", {
              done: loadedCount,
              total: totalCount,
            })
          }}
        </span>
      </div>
      <div
        ref="timelineScroll"
        class="tl-board-scroll"
        :class="{ 'is-dragging': timelineDrag }"
        @pointerdown="startTimelineDrag($event)"
        @pointermove="moveTimelineDrag($event)"
        @pointerup="endTimelineDrag($event)"
        @pointercancel="endTimelineDrag($event)"
        @scroll="syncTimelineScroll($event)"
      >
        <div
          class="tl-board-grid"
          :class="'is-zoom-' + zoom"
          :style="timelineStyle"
        >
          <!-- hónapok (egyszer, ragadós) -->
          <div class="tl-month-row">
            <div class="tl-month-spacer"></div>
            <div
              v-for="month in timelineMonths"
              :key="month.key"
              class="tl-month-cell"
              :style="{ gridColumn: `span ${month.days}` }"
            >
              {{ month.label }}
            </div>
          </div>

          <!-- napfejléc (egyszer, ragadós) -->
          <div class="tl-row tl-header-row">
            <div class="tl-label-cell tl-header tl-col-income">
              {{ $t("productionPriority.income") }}
            </div>
            <div class="tl-label-cell tl-header tl-col-deadline">
              {{ $t("productionPriority.deadline") }}
            </div>
            <div class="tl-label-cell tl-header tl-col-priority">
              {{ $t("productionPriority.priority") }}
            </div>
            <div
              v-for="d in days"
              :key="'h-' + d"
              class="tl-day-cell tl-header"
              :class="[dayClass(d), dayHeaderClass(headerBoard, d)]"
              :title="dayHeaderTitle(headerBoard, d)"
            >
              <div class="tl-day-weekday">{{ weekdayLabel(d) }}</div>
              <div class="tl-day-num">{{ dayNumber(d) }}</div>
              <span
                v-if="dayStatusFor(headerBoard, d)"
                class="tl-day-dot"
                :class="`tl-day-dot--${dayStatusFor(headerBoard, d).level}`"
              ></span>
            </div>
          </div>

          <!-- csoportok: a hozzá nem rendeltek elöl -->
          <template v-for="board in orderedBoards" :key="board.column_key">
            <div
              class="tl-group-row"
              :class="{ 'is-unassigned': board.type === 'unassigned' }"
              @click="toggleBoard(board)"
            >
              <div class="tl-label-cell tl-group-label">
                <button
                  type="button"
                  class="tl-group-toggle"
                  :aria-expanded="isBoardExpanded(board) ? 'true' : 'false'"
                  :title="
                    isBoardExpanded(board)
                      ? $t('productionPriority.hideProducts')
                      : $t('productionPriority.showProducts')
                  "
                  @click.stop="toggleBoard(board)"
                >
                  {{ isBoardExpanded(board) ? "▾" : "▸" }}
                </button>
                <span class="tl-group-name" :title="board.name">{{
                  board.name
                }}</span>
                <span class="tl-group-type" :class="'type-' + board.type">
                  {{ groupTypeLabel(board.type) }}
                </span>
                <span
                  v-if="board.type === 'partner' && board.capacity_hours"
                  class="tl-group-capacity"
                >
                  {{
                    $t("productionPriority.capacityOf", {
                      used: board.used_hours,
                      total: board.capacity_hours,
                    })
                  }}
                </span>
                <span v-if="boardHours(board) !== null" class="tl-group-hours">
                  {{
                    $t("productionPriority.remainingHours", {
                      hours: boardHours(board),
                    })
                  }}
                </span>
                <span class="tl-group-count">{{
                  searchTerm
                    ? visibleRows(board).length + "/" + localRows(board).length
                    : localRows(board).length
                }}</span>
              </div>
              <div
                v-for="d in days"
                :key="'g-' + board.column_key + '-' + d"
                class="tl-day-cell tl-group-cell"
                :class="dayClass(d)"
              ></div>
              <!-- zárt állapotban egy összevont sáv: meddig van tele -->
              <div
                v-if="
                  !searchTerm && !isBoardExpanded(board) && boardSpan(board)
                "
                class="tl-bars"
              >
                <div
                  class="tl-group-bar"
                  :class="{ 'is-narrow': boardBarNarrow(board) }"
                  :style="boardBarStyle(board)"
                  :title="boardBarTitle(board)"
                >
                  <span class="tl-group-bar-text">{{
                    boardBarLabel(board)
                  }}</span>
                </div>
              </div>
            </div>

            <draggable
              v-if="isBoardExpanded(board)"
              :list="visibleRows(board)"
              :disabled="!!searchTerm"
              @change="onRowChange(board, $event)"
              group="production-priority"
              item-key="id"
              class="tl-rows"
              :class="{ 'is-unassigned': board.type === 'unassigned' }"
              :animation="150"
              handle=".tl-row-handle"
              :filter="dragFilter"
            >
              <template #item="{ element: row }">
                <div
                  class="tl-row-wrapper"
                  :class="{ 'tl-row-wrapper--expanded': isRowExpanded(row) }"
                >
                  <div
                    class="tl-row"
                    :class="{ 'tl-row--scheduled': row.is_scheduled }"
                  >
                    <div class="tl-label-cell tl-col-income">
                      <span
                        class="tl-row-handle"
                        :title="$t('productionPriority.drag')"
                        >⠿</span
                      >
                      <button
                        type="button"
                        class="tl-row-toggle"
                        :aria-expanded="isRowExpanded(row)"
                        :title="
                          isRowExpanded(row)
                            ? $t('productionPriority.hideProducts')
                            : $t('productionPriority.showProducts')
                        "
                        @click="toggleRow(row)"
                      >
                        <span class="tl-row-toggle-icon">{{
                          isRowExpanded(row) ? "▾" : "▸"
                        }}</span>
                      </button>
                      <a
                        class="tl-order-link"
                        :href="orderUrl(row)"
                        target="_blank"
                        rel="noopener"
                        :title="
                          (orderTitle(row) ? orderTitle(row) + ' · ' : '') +
                          (row.pretty_id || '#' + row.id)
                        "
                      >
                        <span v-if="orderTitle(row)" class="tl-order-name">{{
                          orderTitle(row)
                        }}</span>
                        <span class="tl-order-num">
                          {{ row.pretty_id || "#" + row.id }}
                          <span
                            class="tl-row-toggle-count"
                            v-if="row.items?.length"
                            >({{ row.items.length }})</span
                          >
                        </span>
                      </a>
                    </div>
                    <div class="tl-label-cell tl-col-deadline">
                      <label
                        class="tl-deadline"
                        :title="$t('productionPriority.deadline')"
                      >
                        <span
                          class="tl-deadline-text"
                          :class="{
                            'is-late': isOverdue(row),
                            'is-empty': !rowDeadline(row),
                          }"
                          >{{
                            rowDeadline(row)
                              ? formatDate(rowDeadline(row))
                              : isOrderLoading(row)
                              ? "…"
                              : "–"
                          }}</span
                        >
                        <input
                          type="date"
                          class="tl-deadline-native"
                          :value="rowDeadline(row) || ''"
                          :key="rowDeadline(row) || 'empty'"
                          @change="
                            saveRow(row, 'deadline', $event.target.value)
                          "
                        />
                      </label>
                    </div>
                    <div class="tl-label-cell tl-col-priority">
                      <select
                        class="tl-priority-badge"
                        :class="`tl-priority-badge--${
                          row.priority || 'normal'
                        }`"
                        :style="priorityStyle(row.priority)"
                        :value="row.priority || 'normal'"
                        :key="row.priority || 'empty'"
                        :title="$t('productionPriority.priority')"
                        @change="saveRow(row, 'priority', $event.target.value)"
                      >
                        <option value="low">
                          {{ $t("productionPriority.priorityLow") }}
                        </option>
                        <option value="normal">
                          {{ $t("productionPriority.priorityNormal") }}
                        </option>
                        <option value="high">
                          {{ $t("productionPriority.priorityHigh") }}
                        </option>
                      </select>
                    </div>
                    <div
                      v-for="d in days"
                      :key="row.id + '-' + d"
                      class="tl-day-cell"
                      :class="dayClass(d)"
                    ></div>
                    <div class="tl-bars">
                      <div
                        v-if="rowDeadline(row) && deadlineDayIndex(row) >= 0"
                        class="tl-deadline-line"
                        :class="{ 'tl-deadline-line--overdue': isOverdue(row) }"
                        :style="deadlineLineStyle(row)"
                        :title="
                          $t('productionPriority.deadline') +
                          ': ' +
                          rowDeadline(row)
                        "
                      ></div>
                      <div
                        class="tl-bar"
                        :class="[
                          row.is_scheduled
                            ? 'tl-bar--scheduled'
                            : 'tl-bar--draft',
                          { 'is-overdue': isOverdue(row) },
                        ]"
                        :style="barStyle(row)"
                        :title="barTitle(row)"
                      >
                        <span class="tl-bar-id">{{ barLabel(row) }}</span>
                        <span class="tl-bar-meta">{{ barMeta(row) }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-if="isRowExpanded(row)" class="tl-row-details">
                    <div class="tl-row-details-inner">
                      <div
                        v-for="item in groupedItems(row)"
                        :key="item.key"
                        class="tl-product"
                      >
                        <div class="tl-product-image">
                          <img
                            v-if="variantImageUrl(item)"
                            :src="variantImageUrl(item)"
                            :alt="item.product.name"
                          />
                          <div v-else class="tl-product-image-placeholder">
                            {{
                              (item.product.name || "?").charAt(0).toUpperCase()
                            }}
                          </div>
                        </div>
                        <div class="tl-product-info">
                          <span class="tl-product-serial">
                            <span>{{
                              item.product.serial || item.sku || "–"
                            }}</span>
                            <span v-if="item.color" class="tl-product-color"
                              >· {{ item.color }}</span
                            >
                          </span>
                          <span
                            v-if="item.tracked && item.done >= item.quantity"
                            class="tl-product-done"
                            :title="$t('productionPriority.productDone')"
                            >✓ {{ $t("productionPriority.productDone") }}</span
                          >
                          <span
                            v-else-if="item.tracked && item.done > 0"
                            class="tl-product-progress"
                            :title="$t('productionPriority.productPartial')"
                            >{{ item.done }} / {{ item.quantity }}</span
                          >
                          <span class="tl-product-qty"
                            >× {{ item.quantity }}</span
                          >
                        </div>
                      </div>
                      <div v-if="!row.items?.length" class="tl-product-empty">
                        {{ $t("productionPriority.noProducts") }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>

            <div
              v-if="isBoardExpanded(board) && !visibleRows(board).length"
              class="tl-group-empty"
            >
              <span>{{ $t("productionPriority.noRows") }}</span>
            </div>
          </template>
        </div>
      </div>
      <div v-if="loadingMore" class="timeline-loading-more">
        {{ $t("productionPriority.loading") }}
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import draggable from "vuedraggable";
import http from "@/modules/http";
import tenant from "@/modules/tenant";

// A rendelés címét és a tételek adatait csak egyesével lehet lekérni (a
// prioritás-végpont nem küldi őket), ami 36 rendelésnél fél perc. Ezért a
// böngészőben eltesszük: újranyitáskor a nevek azonnal ott vannak, és csak a
// hiányzó vagy elavult rendeléseket kérjük le újra.
const ORDER_CACHE_KEY = "productionPriority.orderInfo.v1";
const ORDER_CACHE_TTL = 60 * 60 * 1000;

// FONTOS: ezt a data()-ban kell felhasználni, nem a created()-ben. Az
// `immediate: true` figyelő a created() ELŐTT fut, így ott visszaolvasva már
// elindulna a teljes letöltés.
function readOrderCache() {
  try {
    const raw = window.localStorage.getItem(ORDER_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.at) return {};
    if (Date.now() - parsed.at > ORDER_CACHE_TTL) {
      window.localStorage.removeItem(ORDER_CACHE_KEY);
      return {};
    }
    return parsed.data || {};
  } catch (e) {
    return {};
  }
}

export default {
  name: "ProductionTimeline",
  components: { draggable },
  props: {
    days: { type: Array, default: () => [] },
    boards: { type: Array, default: () => [] },
    loadingMore: { type: Boolean, default: false },
  },
  data() {
    return {
      localBoards: [],
      expandedRowIds: new Set(),
      syncingTimelineScroll: false,
      timelineDrag: null,
      dayColWidth: 34,
      // rendelés-id → { title, deadline, items: { tétel_id: {szín, képútvonal} } }
      orderInfo: readOrderCache(),
      expandedBoards: new Set(),
      // nap / hét / hónap nagyítás; alapból a havi, mert az ad áttekintést
      zoom: "Month",
      zoomLevels: ["Day", "Week", "Month"],
      scrollRaf: null,
      lastLoadMore: 0,
      bulkLoading: false,
      bulkTimer: null,
      destroyed: false,
    };
  },
  computed: {
    // a hozzá nem rendelt megrendelések elöl — onnan húzhatók partnerhez
    orderedBoards() {
      return (this.boards || [])
        .slice()
        .sort(
          (a, b) =>
            (a.type === "unassigned" ? 0 : 1) -
            (b.type === "unassigned" ? 0 : 1)
        );
    },
    // a közös napfejléc naptára: a raktár, ha van
    headerBoard() {
      const list = this.boards || [];
      return (
        list.find((b) => b.type === "warehouse" && b.day_status) ||
        list.find((b) => b.day_status) ||
        list[0] ||
        null
      );
    },
    timelineStyle() {
      return {
        "--tl-days": this.days.length,
        "--tl-daycol": this.dayColWidth + "px",
      };
    },
    dragFilter() {
      return ".tl-row--scheduled";
    },
    today() {
      return dayjs().format("YYYY-MM-DD");
    },
    // A felső keresősáv értéke (a TopBar 3 karaktertől írja a store-ba)
    searchTerm() {
      const q = this.$store.state.topBar?.q;
      return q ? String(q).trim().toLowerCase() : "";
    },
    // a jelzőben az eredeti írásmód látszódjon
    searchLabel() {
      const q = this.$store.state.topBar?.q;
      return q ? String(q).trim() : "";
    },
    searchHitCount() {
      if (!this.searchTerm) return 0;
      return (this.boards || [])
        .flatMap((b) => b.rows || [])
        .filter((r) => this.matchesSearch(r)).length;
    },
    totalCount() {
      return (this.boards || []).reduce((a, b) => a + (b.rows || []).length, 0);
    },
    // Csak a most látható sorokat számoljuk: a gyorsítótárban lehetnek olyan
    // rendelések is, amik ebben a nézetben nem szerepelnek.
    // Összesítő a felső csíkra: mennyi munka van bent és meddig tart
    summary() {
      const rows = (this.boards || []).flatMap((b) => b.rows || []);
      let hours = 0;
      rows.forEach((r) => {
        const h = this.remainingHours(r);
        if (h !== null) hours += h;
      });
      const ends = rows
        .map((r) => r.end_date)
        .filter(Boolean)
        .sort();
      const lastEnd = ends.length ? ends[ends.length - 1] : null;
      const windowEnd = this.days.length
        ? this.days[this.days.length - 1]
        : null;
      return {
        orders: rows.length,
        hours,
        lastEnd,
        // ha a leghosszabb sáv a nézet széléig ér, a befejezés nem fér bele
        clamped: !!(lastEnd && windowEnd && lastEnd >= windowEnd),
      };
    },
    loadedCount() {
      return (this.boards || [])
        .flatMap((b) => b.rows || [])
        .filter((r) => this.orderInfo[r.operation_id || r.id]).length;
    },
    mediaServer() {
      return tenant.getAppMediaUrl ? tenant.getAppMediaUrl() : "";
    },
    timelineMonths() {
      const months = [];
      this.days.forEach((date) => {
        const value = dayjs(date);
        const key = value.format("YYYY-MM");
        const current = months[months.length - 1];
        if (current && current.key === key) {
          current.days += 1;
          return;
        }
        months.push({
          key,
          days: 1,
          label: new Intl.DateTimeFormat(undefined, {
            month: "long",
            year: "numeric",
          }).format(value.toDate()),
        });
      });
      return months;
    },
  },
  watch: {
    boards: {
      immediate: true,
      handler(boards) {
        this.syncLocalBoards(boards);
        this.remeasureSoon();
        this.$nextTick(() => this.fillViewport());
        this.loadAllOrderInfo(boards);
        const validIds = new Set(
          (boards || []).flatMap((b) => (b.rows || []).map((r) => r.id))
        );
        const next = new Set();
        this.expandedRowIds.forEach((id) => {
          if (validIds.has(id)) next.add(id);
        });
        this.expandedRowIds = next;
      },
    },
  },
  created() {
    this.localBoards = [];
    this.syncLocalBoards(this.boards || []);
    this.$nextTick(() => this.measureDayColumn());
    this._dayColResize = () => this.measureDayColumn();
    window.addEventListener("resize", this._dayColResize);
  },
  unmounted() {
    this.destroyed = true;
    if (this.bulkTimer) clearTimeout(this.bulkTimer);
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    if (this._dayColResize)
      window.removeEventListener("resize", this._dayColResize);
  },
  methods: {
    remeasureSoon() {
      this.$nextTick(() => this.measureDayColumn());
    },
    syncLocalBoards(boards) {
      // Build a map of incoming boards by column_key.
      const incoming = new Map();
      (boards || []).forEach((b) => {
        incoming.set(b.column_key, b.rows || []);
      });

      // For each existing local board, replace its rows in place so that
      // vuedraggable (which holds the array reference internally) sees the
      // updated items.
      this.localBoards.forEach((localBoard) => {
        const newRows = incoming.get(localBoard.column_key);
        if (newRows === undefined) {
          // Board removed: clear its rows in place
          localBoard.rows.splice(0, localBoard.rows.length);
          return;
        }
        const oldRows = localBoard.rows;
        // Replace items in place: remove extras, then patch existing items
        // and add new ones.
        while (oldRows.length > newRows.length) {
          oldRows.pop();
        }
        for (let i = 0; i < newRows.length; i++) {
          if (i < oldRows.length) {
            // Patch in place to keep the reference stable for vuedraggable.
            // Use $set/$delete so Vue 2 observes every field change and
            // re-renders the row content (deadline/priority inputs included).
            const target = oldRows[i];
            const source = newRows[i];
            Object.keys(target).forEach((k) => {
              if (!(k in source)) this.$delete(target, k);
            });
            Object.keys(source).forEach((k) => {
              this.$set(target, k, source[k]);
            });
          } else {
            oldRows.push(newRows[i]);
          }
        }
      });

      // For any incoming boards that don't exist locally yet, append them.
      (boards || []).forEach((b) => {
        if (!this.localBoards.some((lb) => lb.column_key === b.column_key)) {
          this.localBoards.push({
            column_key: b.column_key,
            rows: (b.rows || []).slice(),
          });
        }
      });
    },
    localRows(board) {
      const found = this.localBoards.find(
        (b) => b.column_key === board.column_key
      );
      return found ? found.rows : [];
    },
    startTimelineDrag(event) {
      if (event.button !== 0) return;
      if (event.target.closest("button, input, select, a, .tl-row-handle")) {
        return;
      }

      const scroller = event.currentTarget;
      if (scroller.scrollWidth <= scroller.clientWidth) return;

      this.timelineDrag = {
        scroller,
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: scroller.scrollLeft,
      };
      scroller.setPointerCapture?.(event.pointerId);
    },
    moveTimelineDrag(event) {
      if (
        !this.timelineDrag ||
        event.pointerId !== this.timelineDrag.pointerId
      ) {
        return;
      }
      this.timelineDrag.scroller.scrollLeft =
        this.timelineDrag.startScrollLeft -
        (event.clientX - this.timelineDrag.startX);
      event.preventDefault();
    },
    endTimelineDrag(event) {
      if (
        !this.timelineDrag ||
        event.pointerId !== this.timelineDrag.pointerId
      ) {
        return;
      }
      this.timelineDrag.scroller.releasePointerCapture?.(event.pointerId);
      this.timelineDrag = null;
    },
    syncTimelineScroll(event) {
      const source = event.currentTarget;
      const left = source.scrollLeft;
      // A görgetés minden képkockán tüzel; a szinkronizálást rAF-be tesszük,
      // különben a sok elrendezés-számítás akadozóvá teszi a görgetést.
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        const scrollers = Array.isArray(this.$refs.timelineScroll)
          ? this.$refs.timelineScroll
          : [this.$refs.timelineScroll];
        scrollers.forEach((scroller) => {
          if (scroller && scroller !== source) scroller.scrollLeft = left;
        });
      });
      // Az újabb időszak kérése legfeljebb másodpercenként egyszer indulhat.
      if (left + source.clientWidth < source.scrollWidth - 300) return;
      if (this.loadingMore) return;
      const now = Date.now();
      if (now - this.lastLoadMore < 1000) return;
      this.lastLoadMore = now;
      this.$emit("load-more");
    },
    isRowExpanded(row) {
      return this.expandedRowIds.has(row.id);
    },
    toggleRow(row) {
      const next = new Set(this.expandedRowIds);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.add(row.id);
        this.loadOrderDetails(row);
      }
      this.expandedRowIds = next;
    },
    // Rendelésenként egy kérés a bejövő végpontra. A prioritás-végpont nem küld
    // sem címet, sem színt, a `product.image` pedig a termék alapképe (nem a
    // megrendelt variánsé) — ezek mind itt vannak. Kulcs: item_operation_id.
    fetchOrderInfo(id) {
      if (!id || id in this.orderInfo) return Promise.resolve();
      this.orderInfo[id] = null; // folyamatban — ne kérjük le kétszer
      return http
        .fetch("/warehouses/income/" + id + "?order[desc]=0&order[row]=")
        .then((d) => {
          const data = d?.data || {};
          const items = {};
          for (const it of data.items || []) {
            items[it.item_operation_id] = {
              color: it.characteristics_1 || null,
              image: it.image || null,
              quantity: Number(it.quantity) || 0,
              left: Number(it.left_incoming) || 0,
              time: Number(it.time) || 0,
              timeLeft: Number(it.time_left) || 0,
            };
          }
          // A `time_left` a műveleti visszajelzésből jön, nem a bevételezésből:
          // a már bevételezett tételeknél gyakran benne marad a teljes idő.
          // Ezeket kivesszük, különben a hátralévő idő felfelé torzít.
          let remaining = 0;
          let stale = 0;
          for (const it of Object.values(items)) {
            const finished = it.left === 0 && it.quantity > 0;
            const notReported = finished && Math.abs(it.time - it.timeLeft) < 1;
            if (notReported) stale += it.timeLeft;
            else remaining += it.timeLeft;
          }
          this.orderInfo[id] = {
            title: data.title || null,
            deadline: data.deadline_date || null,
            remaining,
            stale,
            items,
          };
        })
        .catch(() => {
          this.orderInfo[id] = {
            title: null,
            deadline: null,
            remaining: 0,
            stale: 0,
            items: {},
          };
        });
    },
    loadOrderDetails(order) {
      this.fetchOrderInfo(order.operation_id || order.id);
    },
    // A címekhez minden rendelést le kell kérni, mert a prioritás-végpont nem
    // küldi őket. Egyesével, 250 ms szünettel: párhuzamosan a szerver
    // rate limitbe fut ("Too Many Attempts"), így viszont végigmegy.
    persistOrderCache() {
      try {
        const data = {};
        for (const [id, info] of Object.entries(this.orderInfo)) {
          if (info) data[id] = info;
        }
        window.localStorage.setItem(
          ORDER_CACHE_KEY,
          JSON.stringify({ at: Date.now(), data })
        );
      } catch (e) {
        // tele a tároló — a gyorsítótár elhagyható, a működés nem múlik rajta
      }
    },
    loadAllOrderInfo(boards) {
      const ids = [
        ...new Set(
          (boards || [])
            .flatMap((b) => b.rows || [])
            .map((r) => r.operation_id || r.id)
            .filter((id) => id && !(id in this.orderInfo))
        ),
      ];
      if (!ids.length || this.bulkLoading) return;
      this.bulkLoading = true;
      const next = () => {
        const id = ids.shift();
        if (id === undefined || this.destroyed) {
          this.bulkLoading = false;
          this.persistOrderCache();
          return;
        }
        this.fetchOrderInfo(id).then(() => {
          this.bulkTimer = setTimeout(next, 250);
        });
      };
      next();
    },
    // Cikkszámra, SKU-ra, rendelésszámra és a megrendelés nevére keresünk
    matchesSearch(row) {
      const q = this.searchTerm;
      if (!q) return true;
      const id = String(row.pretty_id || row.id || "").toLowerCase();
      if (id.includes(q)) return true;
      const title = (this.orderTitle(row) || "").toLowerCase();
      if (title.includes(q)) return true;
      return (row.items || []).some((it) => {
        const serial = String(it.product?.serial || "").toLowerCase();
        const sku = String(it.sku || "").toLowerCase();
        return serial.includes(q) || sku.includes(q);
      });
    },
    isBoardExpanded(board) {
      // Kereséskor magától kinyílik, ami tartalmaz találatot
      if (this.searchTerm) return this.visibleRows(board).length > 0;
      return this.expandedBoards.has(board.column_key);
    },
    // A csoportban megjelenítendő sorok (keresésnél szűrve)
    visibleRows(board) {
      const rows = this.localRows(board);
      if (!this.searchTerm) return rows;
      return rows.filter((r) => this.matchesSearch(r));
    },
    toggleBoard(board) {
      const next = new Set(this.expandedBoards);
      if (next.has(board.column_key)) next.delete(board.column_key);
      else next.add(board.column_key);
      this.expandedBoards = next;
    },
    // A csoport lefoglaltsága: a legkorábbi kezdéstől a legkésőbbi befejezésig
    boardSpan(board) {
      const rows = board.rows || [];
      const starts = rows
        .map((r) => r.start_date)
        .filter(Boolean)
        .sort();
      const ends = rows
        .map((r) => r.end_date)
        .filter(Boolean)
        .sort();
      if (!starts.length || !ends.length) return null;
      return { start_date: starts[0], end_date: ends[ends.length - 1] };
    },
    boardBarStyle(board) {
      const span = this.boardSpan(board);
      return span ? this.barStyle(span) : { display: "none" };
    },
    // Ha a sáv rövidebb, mint a felirat, a szöveg a sáv mellé kerül
    boardBarNarrow(board) {
      const span = this.boardSpan(board);
      if (!span) return false;
      const s = this.days.indexOf(span.start_date);
      const e = this.days.indexOf(span.end_date);
      if (s < 0 || e < 0) return false;
      return (e - s + 1) * this.dayColWidth < 200;
    },
    boardBarLabel(board) {
      const span = this.boardSpan(board);
      if (!span) return "";
      const parts = [
        this.$t("productionPriority.bookedUntil", {
          date: this.formatDate(span.end_date),
        }),
      ];
      const h = this.boardHours(board);
      if (h !== null) {
        parts.push(this.$t("productionPriority.remainingHours", { hours: h }));
      }
      return parts.join(" · ");
    },
    boardBarTitle(board) {
      const span = this.boardSpan(board);
      if (!span) return "";
      return (
        board.name +
        "\n" +
        this.formatDate(span.start_date) +
        " → " +
        this.formatDate(span.end_date) +
        "\n" +
        (board.rows || []).length +
        " " +
        this.$t("productionPriority.summaryOrders")
      );
    },
    // A csoport hátralévő munkája órában (null, amíg semmi nem töltött be)
    boardHours(board) {
      const rows = board.rows || [];
      let sum = 0;
      let known = 0;
      rows.forEach((r) => {
        const h = this.remainingHours(r);
        if (h !== null) {
          sum += h;
          known++;
        }
      });
      return known ? sum : null;
    },
    orderTitle(order) {
      return this.orderInfo[order.operation_id || order.id]?.title || null;
    },
    // Elkészült darabszám: a megrendelt mennyiség mínusz a még hátralévő
    itemDone(meta) {
      if (!meta) return 0;
      return Math.max(0, (meta.quantity || 0) - (meta.left || 0));
    },
    itemMeta(order, item) {
      const info = this.orderInfo[order.operation_id || order.id];
      return (info && info.items && info.items[item.id]) || null;
    },
    variantImageUrl(entry) {
      if (entry.imagePath)
        return (this.mediaServer || "") + "/" + entry.imagePath;
      return this.productImageUrl(entry.product?.image);
    },
    // Cikkszám + szín szerint összevonva (a méretek összeadva)
    groupedItems(order) {
      const map = new Map();
      for (const item of order.items || []) {
        const meta = this.itemMeta(order, item);
        const color = meta?.color || null;
        const key =
          (item.product?.id ?? item.product?.serial ?? item.sku) +
          "|" +
          (color || "");
        const found = map.get(key);
        if (found) {
          found.quantity += Number(item.quantity) || 0;
          found.done += this.itemDone(meta);
          found.tracked = found.tracked || !!meta;
        } else {
          map.set(key, {
            key,
            product: item.product || {},
            color,
            imagePath: meta?.image || null,
            quantity: Number(item.quantity) || 0,
            done: this.itemDone(meta),
            tracked: !!meta,
          });
        }
      }
      return [...map.values()];
    },
    productImageUrl(image) {
      if (!image?.path || !image?.name) return null;
      return (
        (this.mediaServer || "") +
        "/" +
        image.path +
        "/262-" +
        image.name +
        ".webp"
      );
    },
    priorityStyle(priority) {
      const map = {
        low: { bg: "#F3F4F5", color: "#4B506D", border: "#DFE0EB" },
        normal: { bg: "#E7EAFF", color: "#3751FF", border: "#C6D0FF" },
        high: { bg: "#FFE3E3", color: "#B3261E", border: "#F5C2C2" },
      };
      const c = map[priority] || map.normal;
      return {
        backgroundColor: c.bg,
        color: c.color,
        borderColor: c.border,
      };
    },
    // A csoportsor típusjelvénye (RAKTÁR / PARTNER / nincs hozzárendelve)
    groupTypeLabel(type) {
      return this.boardTypeLabel(type);
    },
    formatDate(date) {
      if (!date) return "–";
      const d = dayjs(date);
      return d.isValid() ? d.format("YYYY. MM. DD.") : "–";
    },
    // A sáv másodlagos felirata: véglegesített/tervezett · hossz napokban
    barMeta(order) {
      const parts = [
        this.$t(
          order.is_scheduled
            ? "productionPriority.timelineScheduled"
            : "productionPriority.timelineDraft"
        ),
      ];
      if (order.start_date && order.end_date) {
        const days =
          dayjs(order.end_date).diff(dayjs(order.start_date), "day") + 1;
        if (days > 0) {
          parts.push(this.$t("productionPriority.dayCount", { n: days }));
        }
      }
      const left = this.remainingHours(order);
      if (left !== null) {
        parts.push(
          this.$t("productionPriority.remainingHours", { hours: left })
        );
      }
      return parts.join(" · ");
    },
    boardTypeLabel(type) {
      if (type === "warehouse") return this.$t("productionPriority.warehouse");
      if (type === "partner") return this.$t("productionPriority.partner");
      return this.$t("productionPriority.unassigned");
    },
    boardBadgeClass(type) {
      if (type === "partner") return "bg-info";
      if (type === "warehouse") return "bg-secondary";
      return "bg-light text-dark";
    },
    onRowChange(board, event) {
      const rows = this.localRows(board);
      if (event.added) {
        const row = event.added.element;
        const newIndex =
          event.added.newIndex !== undefined
            ? event.added.newIndex
            : rows.findIndex((r) => r.id === row.id);
        http
          .fetch("/production/priority/move", {
            operation_id: row.id,
            location_type: board.type,
            location_id: board.id,
            new_index: newIndex,
          })
          .then(() => this.$emit("reload"))
          .catch(() => {
            this.$emit("reload");
          });
      } else if (event.moved) {
        http
          .fetch("/production/priority/reorder", {
            location_type: board.type,
            location_id: board.id,
            ordered_ids: rows.map((r) => r.id),
          })
          .then(() => this.$emit("reload"))
          .catch(() => {
            this.$emit("reload");
          });
      }
    },
    saveRow(row, field, value) {
      delete this.orderInfo[row.operation_id || row.id];
      this.persistOrderCache();
      const payload = { operation_id: row.id };
      if (field === "deadline") {
        payload.deadline_date = value || "";
      } else {
        payload.priority = value === "" || value == null ? "" : value;
      }
      http
        .fetch("/production/priority/operation", payload)
        .then(() => this.$emit("reload"))
        .catch(() => {
          this.$emit("reload");
        });
    },
    weekdayLabel(date) {
      return dayjs(date).format("ddd");
    },
    dayNumber(date) {
      return dayjs(date).format("D");
    },
    isWeekend(date) {
      const day = dayjs(date).day();
      return day === 0 || day === 6;
    },
    isToday(date) {
      return date === this.today;
    },
    dayClass(date) {
      return {
        "is-weekend": this.isWeekend(date),
        "is-today": this.isToday(date),
      };
    },
    dayStatusFor(board, day) {
      const list = board && board.day_status;
      if (!Array.isArray(list)) return null;
      return list.find((d) => d.day === day) || null;
    },
    dayHeaderClass(board, day) {
      const ds = this.dayStatusFor(board, day);
      if (!ds) return {};
      return {
        [`tl-day-${ds.level}`]: true,
      };
    },
    dayHeaderTitle(board, day) {
      const ds = this.dayStatusFor(board, day);
      if (!ds) return "";
      const lines = [day];
      if (ds.holiday) {
        lines.push(
          this.$t("productionPriority.dayHoliday") + ": " + ds.holiday.name
        );
      }
      lines.push(
        this.$t("productionPriority.teamCount", { count: ds.team_count })
      );
      if (ds.leaves.length) {
        const names = ds.leaves
          .map((l) => {
            const reason =
              l.reason && l.reason.name ? " (" + l.reason.name + ")" : "";
            return (l.name || "?") + reason;
          })
          .join(", ");
        lines.push(
          this.$t("productionPriority.leavesCount", {
            count: ds.leaves.length,
          }) +
            ": " +
            names
        );
      }
      return lines.join("\n");
    },
    // A nap-oszlop szélessége úgy, hogy nagyjából 5 hét látszódjon egyszerre
    setZoom(z) {
      if (this.zoom === z) return;
      this.zoom = z;
      this.$nextTick(() => {
        this.measureDayColumn();
        this.fillViewport();
      });
    },
    measureDayColumn() {
      if (this.zoom === "Week") {
        this.dayColWidth = 12;
        return;
      }
      if (this.zoom === "Month") {
        this.dayColWidth = 5;
        return;
      }
      const sc = this.$refs.timelineScroll;
      const el = Array.isArray(sc) ? sc[0] : sc;
      if (!el) return;
      const labelWidth = 190 + 112 + 88;
      const avail = el.clientWidth - labelWidth;
      if (avail <= 0) return;
      const target = Math.floor(avail / 35);
      const next = Math.max(22, Math.min(48, target));
      if (next !== this.dayColWidth) this.dayColWidth = next;
    },
    // Kizoomolva a betöltött időszak rövidebb lehet, mint a nézet szélessége —
    // ilyenkor kérünk még, hogy ne maradjon üres sáv a jobb szélen.
    fillViewport() {
      const sc = this.$refs.timelineScroll;
      const el = Array.isArray(sc) ? sc[0] : sc;
      if (!el || this.loadingMore) return;
      const max = 400;
      if (this.days.length >= max) return;
      // A rács min-width: 100%, ezért a scrollWidth akkor is a nézet szélessége,
      // ha a napok jóval kevesebb helyet foglalnak — a tartalmat számoljuk ki.
      const labelWidth = 190 + 112 + 88;
      const content = labelWidth + this.days.length * this.dayColWidth;
      const hiany = el.clientWidth - content;
      if (hiany <= 40) return;
      const kell = Math.ceil(hiany / this.dayColWidth) + 7;
      this.$emit("load-more", Math.min(kell, max - this.days.length));
    },
    // A határidő a tételeken van, a soron mindig null — a legkorábbit vesszük
    // Csak mérvadó forrásból: a rendelés saját határideje. A tételekből
    // korábban származtattuk, de az hibás értéket is hozott (#10748 → 1900),
    // és betöltéskor átugrott a helyesre — ezért innen kikerült.
    rowDeadline(order) {
      const own = this.orderInfo[order.operation_id || order.id]?.deadline;
      if (own) return String(own).slice(0, 10);
      return order.deadline_date || null;
    },
    // Amíg a rendelés adatai töltenek, ne látszódjon végleges "nincs határidő"
    isOrderLoading(order) {
      return !this.orderInfo[order.operation_id || order.id];
    },
    // Hátralévő munka órában, a már kész tételek levonása után
    remainingHours(order) {
      const info = this.orderInfo[order.operation_id || order.id];
      if (!info) return null;
      return Math.round(info.remaining / 60);
    },
    // A kész termék megrendelése a bejövő oldalon van (/production/... az alapanyag)
    orderUrl(order) {
      return "/incoming/" + (order.operation_id || order.id);
    },
    isOverdue(order) {
      const d = this.rowDeadline(order);
      if (!d) return false;
      return dayjs(d).isBefore(dayjs(), "day");
    },
    barStyle(order) {
      const total = this.days.length;
      if (!total) return { display: "none" };
      const s = this.days.indexOf(order.start_date);
      const e = this.days.indexOf(order.end_date);
      if (s < 0 || e < 0) return { display: "none" };
      const start = Math.max(0, Math.min(s, total - 1));
      const end = Math.max(start, Math.min(e, total - 1));
      return {
        left: (start / total) * 100 + "%",
        width: ((end - start + 1) / total) * 100 + "%",
      };
    },
    deadlineDayIndex(order) {
      const d = this.rowDeadline(order);
      if (!d) return -1;
      return this.days.indexOf(d);
    },
    deadlineLineStyle(order) {
      const total = this.days.length;
      if (!total) return { display: "none" };
      const idx = this.deadlineDayIndex(order);
      if (idx < 0) return { display: "none" };
      const left = (idx / total) * 100;
      return {
        left: `calc(${left}% + ${(1 / total) * 100}% )`,
      };
    },
    barLabel(order) {
      return order.pretty_id || "#" + order.id;
    },
    barTitle(order) {
      const lines = [
        order.pretty_id || "#" + order.id,
        order.estimated_hours + "h",
        order.start_date +
          (order.end_date !== order.start_date ? " → " + order.end_date : ""),
      ];
      if (order.deadline_date) {
        lines.push("deadline: " + order.deadline_date);
      }
      order.items.forEach((item) => {
        lines.push(
          (item.product?.name || item.sku || "item") + " × " + item.quantity
        );
      });
      return lines.join("\n");
    },
  },
};
</script>

<style scoped>
.production-timeline {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.timeline-legend {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--gray);
  padding: 0 2px 10px;
}
.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
}
.legend-item .tl-day-dot--all,
.legend-item .tl-day-dot--some,
.legend-item .tl-day-dot--none,
.legend-item .tl-day-dot--holiday {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}
/* ── EGYESÍTETT IDŐVONAL ── */
.tl-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  color: #495057;
  background: var(--extra-light);
  border-bottom: 1px solid var(--divider);
}
.tl-summary strong {
  font-weight: 700;
  color: #212529;
}
.tl-summary-sep {
  color: #adb5bd;
}
.tl-summary-warn {
  color: #b3261e;
  font-weight: 600;
}
.tl-summary-search {
  padding: 1px 9px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  color: #084298;
  background: #cfe2ff;
  border: 1px solid #9ec5fe;
}
.tl-zoom {
  display: inline-flex;
  margin-left: auto;
  border: 1px solid var(--divider);
  border-radius: 6px;
  overflow: hidden;
  background: var(--white-color);
}
.tl-zoom-btn {
  border: none;
  background: transparent;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
}
.tl-zoom-btn + .tl-zoom-btn {
  border-left: 1px solid var(--divider);
}
.tl-zoom-btn.is-active {
  background: #713ae8;
  color: #fff;
}
/* kizoomolva a napcímkék olvashatatlanok lennének — csak a hónapsor marad */
.tl-board-grid.is-zoom-Week .tl-day-weekday,
.tl-board-grid.is-zoom-Month .tl-day-weekday,
.tl-board-grid.is-zoom-Month .tl-day-num,
.tl-board-grid.is-zoom-Month .tl-day-dot {
  display: none;
}
.tl-board-grid.is-zoom-Week .tl-day-num {
  font-size: 9px;
}
.tl-board-grid.is-zoom-Week .tl-bar-meta,
.tl-board-grid.is-zoom-Month .tl-bar-meta {
  display: none;
}
.tl-summary-loading {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 12px;
  font-size: 11.5px;
  font-weight: 600;
  color: #6c757d;
}
/* csoport nyitó gomb */
.tl-group-toggle {
  border: none;
  background: transparent;
  padding: 0 6px 0 0;
  font-size: 12px;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
}
.tl-group-row {
  cursor: pointer;
}
.tl-group-bar {
  position: absolute;
  top: 6px;
  bottom: 6px;
  border-radius: 5px;
  background: repeating-linear-gradient(
    135deg,
    #6c5ce7,
    #6c5ce7 7px,
    #7d6ff0 7px,
    #7d6ff0 14px
  );
  display: flex;
  align-items: center;
  /* balra: a sáv gyakran kifut a nézetből, jobbra a felirat nem látszana */
  justify-content: flex-start;
  padding: 0 10px;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
}
.tl-group-bar.is-narrow {
  overflow: visible;
}
.tl-group-bar.is-narrow .tl-group-bar-text {
  position: absolute;
  left: 100%;
  margin-left: 8px;
  color: #4b506d;
  text-shadow: none;
}
.tl-group-bar-text {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}
.tl-group-hours {
  margin-left: 10px;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  white-space: nowrap;
}
.tl-loading-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #713ae8;
  animation: tl-pulse 1s ease-in-out infinite;
}
@keyframes tl-pulse {
  0%,
  100% {
    opacity: 0.25;
  }
  50% {
    opacity: 1;
  }
}
.tl-frame {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--divider);
  border-radius: 8px;
  overflow: hidden;
  background: var(--white);
}
.tl-board-grid > .tl-month-row {
  position: sticky;
  top: 0;
  z-index: 30;
  height: 30px;
  align-items: center;
}
.tl-board-grid > .tl-header-row {
  position: sticky;
  top: 30px;
  z-index: 28;
  background: var(--extra-light);
}
.tl-product-color {
  margin-left: 6px;
  font-weight: 600;
  color: var(--gray);
}

/* megrendelés: név felül (link, új lap), alatta a sorszám */
.tl-order-link {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.15;
  text-decoration: none;
}
.tl-order-name {
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tl-order-num {
  font-size: 0.625rem;
  font-weight: 600;
  color: var(--gray);
  white-space: nowrap;
}
.tl-order-link:hover .tl-order-name,
.tl-order-link:hover .tl-order-num {
  color: #713ae8;
  text-decoration: underline;
}

/* fejléc-címkék és a rövid értékek középre */
.tl-board-grid > .tl-header-row > .tl-label-cell {
  justify-content: center;
  text-align: center;
}
.tl-board-grid .tl-row > .tl-col-deadline,
.tl-board-grid .tl-row > .tl-col-priority {
  justify-content: center;
}
.tl-board-grid .tl-row > .tl-col-priority .tl-priority-badge {
  width: auto;
  min-width: 72px;
  text-align: center;
}
/* a fejléc ragadós bal cellái mindennél feljebb */
.tl-board-grid > .tl-month-row > .tl-month-spacer,
.tl-board-grid > .tl-header-row > .tl-label-cell {
  z-index: 32;
}
/* a csoport-sor a sorok fölött, de a fejléc alatt */
.tl-board-grid > .tl-group-row {
  position: relative;
  z-index: 8;
}
/* csoport-sor */
.tl-group-row {
  position: relative;
  display: grid;
  grid-template-columns:
    var(--tl-income)
    var(--tl-deadline)
    var(--tl-priority)
    repeat(var(--tl-days), var(--tl-daycol, 34px));
  height: 34px;
  background: var(--white-color);
  border-bottom: 1px solid var(--divider);
  border-top: 1px solid var(--divider);
}
.tl-group-row > .tl-group-label {
  grid-column: 1 / span 3;
  position: sticky;
  left: 0;
  z-index: 9;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  min-height: 0;
  background: var(--white-color);
  border-right: 1px solid var(--divider);
  min-width: 0;
}
.tl-group-name {
  font-weight: 700;
  font-size: 0.8125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tl-group-type {
  flex-shrink: 0;
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 2px 6px;
  border-radius: 3px;
  background: var(--bg);
  color: var(--gray);
}
.tl-group-type.type-warehouse {
  background: var(--disabled);
  color: var(--default);
}
.tl-group-type.type-partner {
  background: var(--lgreen);
  color: #0e7a52;
}
.tl-group-type.type-unassigned {
  background: var(--lyellow);
  color: #8a6a00;
}
.tl-group-capacity {
  font-size: 0.625rem;
  color: var(--gray);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tl-group-count {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--gray);
}
.tl-group-cell {
  background: var(--white-color);
}
/* hozzá nem rendelt csoport — sárgával elkülönítve, felül */
.tl-group-row.is-unassigned,
.tl-group-row.is-unassigned .tl-group-label,
.tl-group-row.is-unassigned .tl-group-cell {
  background: #fffbee;
}
.tl-group-row.is-unassigned {
  border-top-color: #f0d98a;
  border-bottom-color: #f0d98a;
}
.tl-rows.is-unassigned .tl-row,
.tl-rows.is-unassigned .tl-label-cell {
  background: #fffefa;
}
.tl-group-empty {
  padding: 10px 12px;
  font-size: 0.75rem;
  color: var(--gray-light);
  position: sticky;
  left: 0;
  width: max-content;
}
/* határidő szövegként, a natív választó fölé húzva */
.tl-deadline {
  position: relative;
  display: block;
  cursor: pointer;
}
.tl-deadline-text {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--black);
  white-space: nowrap;
}
.tl-deadline-text.is-late {
  color: var(--red);
}
.tl-deadline-text.is-empty {
  color: var(--gray-light);
}
.tl-deadline:hover .tl-deadline-text {
  text-decoration: underline dotted;
  text-underline-offset: 3px;
}
.tl-deadline-native {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  border: 0;
  padding: 0;
  cursor: pointer;
}
/* csík-feliratok */
.tl-bar {
  display: flex;
  align-items: center;
  gap: 7px;
}
.tl-bar-id {
  flex-shrink: 0;
}
.tl-bar-meta {
  font-size: 0.5625rem;
  font-weight: 600;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.timeline-loading-more {
  padding: 6px 0;
  color: var(--gray);
  font-size: 0.75rem;
  border-top: 1px solid var(--divider);
  text-align: center;
}
.tl-board {
  margin-bottom: 20px;
  width: 100%;
  min-width: 0;
}
.tl-board-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  background: var(--white-color);
  border: 1px solid var(--divider);
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  box-sizing: border-box;
  min-width: 100%;
}
.tl-board-name {
  font-weight: 700;
  font-size: 0.8125rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tl-board-capacity {
  font-size: 12px;
  color: #6c757d;
}
.tl-board-count {
  margin-left: auto;
  font-size: 12px;
  color: #6c757d;
  background: #f4f5f7;
  border-radius: 10px;
  padding: 0 8px;
  line-height: 18px;
}
.tl-board-grid {
  --tl-income: 190px;
  --tl-deadline: 112px;
  --tl-priority: 88px;
  --tl-label: calc(var(--tl-income) + var(--tl-deadline) + var(--tl-priority));
  display: block;
  width: max-content;
  min-width: 100%;
}
.tl-board-scroll::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}
.tl-board-scroll::-webkit-scrollbar-thumb {
  background-color: #c9cbd6;
  border-radius: 10px;
  border: 2px solid var(--white);
}
.tl-board-scroll::-webkit-scrollbar-thumb:hover {
  background-color: #713ae8;
}
.tl-board-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.tl-board-scroll {
  flex: 1;
  min-height: 0;
  width: 100%;
  min-width: 0;
  overflow: auto;
  overscroll-behavior: contain;
  cursor: grab;
  touch-action: pan-x pan-y;
}
.tl-board-scroll.is-dragging {
  cursor: grabbing;
  user-select: none;
  touch-action: none;
}
.tl-month-row {
  display: grid;
  grid-template-columns:
    var(--tl-income)
    var(--tl-deadline)
    var(--tl-priority)
    repeat(var(--tl-days), var(--tl-daycol, 34px));
  min-height: 30px;
  border: 1px solid #dee2e6;
  border-bottom: none;
  background: #f1f3f5;
  position: sticky;
  top: 0;
  z-index: 5;
}
.tl-month-spacer {
  grid-column: span 3;
  border-right: 1px solid #dee2e6;
  position: sticky;
  left: 0;
  z-index: 6;
  background: #f1f3f5;
}
.tl-month-cell {
  min-width: 0;
  padding: 7px 10px 6px;
  border-right: 1px solid #dee2e6;
  color: #495057;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tl-row {
  position: relative;
  display: grid;
  grid-template-columns:
    var(--tl-income)
    var(--tl-deadline)
    var(--tl-priority)
    repeat(var(--tl-days), var(--tl-daycol, 34px));
  border-bottom: 1px solid #e9ecef;
}
.tl-header-row {
  border-bottom: 1px solid #dee2e6;
  position: sticky;
  top: 30px;
  z-index: 4;
}
.tl-label-cell {
  background: var(--white);
  border-right: 1px solid var(--divider);
  padding: 4px 10px;
  min-height: 38px;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 2;
}
.tl-row > .tl-col-income,
.tl-header-row > .tl-col-income {
  position: sticky;
  left: 0;
  z-index: 6;
}
.tl-row > .tl-col-deadline,
.tl-header-row > .tl-col-deadline {
  position: sticky;
  left: var(--tl-income);
  z-index: 6;
}
.tl-row > .tl-col-priority,
.tl-header-row > .tl-col-priority {
  position: sticky;
  left: calc(var(--tl-income) + var(--tl-deadline));
  z-index: 6;
}
.tl-row > .tl-col-income,
.tl-row > .tl-col-deadline,
.tl-row > .tl-col-priority {
  background: #fff;
}
.tl-row-wrapper--expanded > .tl-row > .tl-col-income,
.tl-row-wrapper--expanded > .tl-row > .tl-col-deadline,
.tl-row-wrapper--expanded > .tl-row > .tl-col-priority {
  background: #f0f7ff;
}
.tl-label-cell.tl-header {
  background: #f8f9fa;
  font-size: 11px;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.tl-col-income {
  gap: 8px;
}
.tl-col-deadline,
.tl-col-priority {
  padding: 4px 8px;
}
.tl-row-handle {
  cursor: grab;
  color: #adb5bd;
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
  user-select: none;
  flex-shrink: 0;
}
.tl-row--scheduled .tl-row-handle {
  cursor: not-allowed;
  opacity: 0.4;
}
.tl-row-handle:active {
  cursor: grabbing;
}
.tl-income {
  min-width: 0;
}
.tl-income-id {
  font-size: 13px;
  font-weight: 600;
}
.tl-row-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid transparent;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--black);
  cursor: pointer;
  text-align: left;
  min-width: 0;
  max-width: 100%;
}
.tl-row-toggle:hover {
  background: #f1f3f5;
  border-color: #dee2e6;
}
.tl-row-toggle:focus {
  outline: none;
  border-color: #0d6efd;
  background: #f1f3f5;
}
.tl-row-toggle-icon {
  color: #6c757d;
  font-size: 11px;
  line-height: 1;
  width: 10px;
  text-align: center;
  flex-shrink: 0;
}
.tl-row-toggle-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tl-row-toggle-count {
  color: #6c757d;
  font-weight: 400;
  font-size: 11px;
  flex-shrink: 0;
}
.tl-row-details {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-top: none;
  padding: 10px 12px;
}
.tl-row-details-inner {
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: sticky;
  left: 0;
  width: var(--tl-label);
  padding: 0 10px 0 26px;
}
.tl-product {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--white);
  border: 1px solid var(--divider);
  border-radius: 6px;
  padding: 4px 8px 4px 4px;
  width: 100%;
}
.tl-product-image {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  background: #f1f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tl-product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.tl-product-image-placeholder {
  font-size: 18px;
  font-weight: 600;
  color: #adb5bd;
}
.tl-product-info {
  display: flex;
  align-items: baseline;
  gap: 8px;
  justify-content: space-between;
  min-width: 0;
  flex: 1;
}
.tl-product-name {
  font-size: 13px;
  font-weight: 600;
  color: #212529;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tl-product-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-size: 11px;
  color: #6c757d;
  margin-top: 2px;
}
.tl-product-serial,
.tl-product-sku {
  background: #f1f3f5;
  padding: 1px 6px;
  border-radius: 3px;
  font-family: monospace;
}
.tl-product-done {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #0f5132;
  background: #d1e7dd;
  border: 1px solid #a3cfbb;
  border-radius: 10px;
  padding: 1px 7px;
  white-space: nowrap;
}
.tl-product-progress {
  font-size: 11px;
  font-weight: 600;
  color: #664d03;
  background: #fff3cd;
  border: 1px solid #ffe69c;
  border-radius: 10px;
  padding: 1px 7px;
  white-space: nowrap;
}
.tl-product-qty {
  font-weight: 600;
  color: #495057;
}
.tl-product-empty {
  font-size: 12px;
  color: #adb5bd;
  font-style: italic;
}
.tl-row-wrapper--expanded > .tl-row {
  background: #f0f7ff;
}
.tl-qty {
  color: #6c757d;
}
.tl-input {
  min-width: 0;
}
/* határidő — látszódjon, hogy szerkeszthető; üresen halvány */
.tl-deadline-input {
  border: 1px solid var(--gray-lightest) !important;
  border-radius: 6px !important;
  background: var(--extra-light) !important;
  padding: 3px 6px !important;
  height: 26px;
  font-size: 0.6875rem !important;
  font-weight: 600;
  color: var(--black);
  box-shadow: none !important;
}
.tl-deadline-input:hover {
  border-color: #a947d6 !important;
}
.tl-deadline-input:focus {
  border-color: var(--gray) !important;
  outline: none;
}
/* üres határidő: a natív "éééé. hh. nn." helyőrző halványan */
.tl-deadline-input.is-empty {
  border-style: dashed !important;
}
.tl-deadline-input.is-empty::-webkit-datetime-edit {
  color: var(--gray-light);
}
.tl-deadline-input::-webkit-calendar-picker-indicator {
  opacity: 0.45;
  cursor: pointer;
}
.tl-deadline-input:hover::-webkit-calendar-picker-indicator {
  opacity: 0.9;
}
.tl-col-priority .tl-input option[value="low"] {
  color: var(--gray);
}
.tl-col-priority .tl-input option[value="normal"] {
  color: var(--default);
}
.tl-col-priority .tl-input option[value="high"] {
  color: var(--red);
  font-weight: 600;
}
.tl-col-priority .tl-input {
  font-weight: 600;
}
.tl-day-cell {
  min-height: 38px;
  border-right: 1px solid var(--divider-graph);
}
.tl-day-cell.is-weekend:not(.tl-header) {
  background: #fbfafb;
}
.tl-day-cell.is-today:not(.tl-header) {
  background: #f5f2ff;
}
.tl-header {
  background: var(--extra-light);
  text-align: center;
  padding: 4px 0;
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--gray);
  line-height: 1.2;
}
.tl-header.is-today {
  background: #f5f2ff;
  color: #713ae8;
}
.tl-header.is-weekend {
  color: var(--red);
}
.tl-day-weekday {
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.tl-day-num {
  font-size: 0.625rem;
  font-weight: 700;
}
.tl-bars {
  position: absolute;
  top: 0;
  bottom: 0;
  left: var(--tl-label);
  right: 0;
}
.tl-deadline-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: repeating-linear-gradient(var(--red) 0 3px, transparent 3px 6px);
  z-index: 3;
  pointer-events: auto;
}
.tl-deadline-line--overdue {
  background: var(--red);
  box-shadow: 0 0 0 1px rgba(241, 43, 44, 0.35);
  animation: tl-deadline-pulse 1.6s ease-in-out infinite;
}
@keyframes tl-deadline-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
.tl-bar {
  position: absolute;
  top: 6px;
  height: 26px;
  min-height: 26px;
  border-radius: 4px;
  padding: 0 6px;
  font-size: 11px;
  line-height: 22px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
}
.tl-bar--scheduled {
  background: var(--default);
  color: #fff;
  border: 1px solid var(--default);
}
.tl-bar--draft {
  background: repeating-linear-gradient(45deg, #fff8e1 0 5px, #fffdf5 5px 10px);
  color: #8a6a00;
  border: 1px solid #f0d98a;
}
.tl-bar.is-overdue {
  border-left: 3px solid var(--red);
}
.tl-bar:hover {
  filter: brightness(0.95);
}
.tl-empty {
  border: 1px solid var(--divider);
  border-top: none;
  border-radius: 0 0 8px 8px;
  background: var(--white);
  padding: 18px 14px;
  font-size: 0.75rem;
  color: var(--gray-light);
  text-align: center;
}
.tl-day-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 2px;
  flex-shrink: 0;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
}
.tl-day-dot--all {
  background: var(--green);
}
.tl-day-dot--some {
  background: var(--yellow);
}
.tl-day-dot--none {
  background: var(--red);
}
.tl-day-dot--holiday {
  background: #713ae8;
}
.tl-header .tl-day-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
}
.tl-day-cell.tl-day-holiday {
  background: #f1eafb;
}
.tl-day-cell.tl-day-none {
  background: rgba(241, 43, 44, 0.07);
}
.tl-day-cell.tl-day-some {
  background: rgba(254, 196, 0, 0.1);
}
.tl-priority-badge {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 1px solid transparent;
  border-radius: 3px;
  padding: 3px 20px 3px 8px;
  font-size: 0.5625rem;
  font-weight: 600;
  line-height: 1.4;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;
  width: 100%;
  max-width: 100px;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3E%3Cpath fill='%236c757d' d='M0 2l4 4 4-4z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 6px center;
  background-size: 8px 8px;
}
.tl-priority-badge:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
}
.tl-priority-badge--low {
  background-color: #f1f3f5;
  color: #6c757d;
  border-color: #dee2e6;
}
.tl-priority-badge--normal {
  background-color: #cfe2ff;
  color: #084298;
  border-color: #9ec5fe;
}
.tl-priority-badge--high {
  background-color: #f8d7da;
  color: #842029;
  border-color: #f1aeb5;
}
</style>
